/* eslint-disable @typescript-eslint/no-invalid-void-type */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import type { FetchBaseQueryError, TagDescription } from '@reduxjs/toolkit/query'

import { requestFeedbackPrompt } from '../../hooks/useFeedbackPrompt/requestFeedbackPrompt'
import type {
  FacebookPostCreateRequest,
  FacebookPostUpdateRequest,
  InstagramPostCreateRequest,
  InstagramPostUpdateRequest,
  PagePostResponse,
  PostProduct,
  PostResponse,
  TiktokPostCreateRequest,
  TiktokPostUpdateRequest,
} from '../../types'
import { sendPostNotification } from '../../utils/sendPostNotification'
import api from '../api'
import { trackId, untrackId } from '../posts/actions'
import type { RootState } from '../store'
import {
  applyPostToPage,
  belongsToCachedList,
  CAN_BE_PLACED_IN_THE_PUBLISHED_LIST,
  matchesCachedList,
  removePostFromPage,
} from './postsCache'
import rootApi from './root'

// remove required title from Product approved back
type PatchProduct<T> = T extends { product?: PostProduct }
  ? Omit<T, 'product'> & { product?: Pick<PostProduct, 'id'> }
  : T

interface Draftable<T> {
  draft?: boolean
  body: T
}

type PostMutationTag = TagDescription<
  'Post-item' | 'Post-statistics' | 'Campaign' | 'Campaign-statistics'
>

export const POST_MUTATION_INVALIDATES: PostMutationTag[] = [
  'Post-item',
  'Post-statistics',
  'Campaign',
  'Campaign-statistics',
]

const PUBLISHING_STREAM_GRACE_SECONDS = 30

const TERMINAL_STATUSES = ['SENT', 'FAILED']

const STREAM_CLOSED = 2

const REOPEN_STREAM_AFTER_MS = 3000

const REOPEN_STREAM_AT_MOST_AFTER_MS = 60000

const SESSION_REJECTED = [401, 403]

const trackedIn = (state: unknown): PostResponse[] => (state as RootState).posts.tracked

const cachedListsIn = (state: unknown): string[] =>
  postsApi.util
    .selectInvalidatedBy(state as never, [{ type: 'Post-item', id: 'LIST' }])
    .map(({ originalArgs }) => originalArgs as string)

export const postsListTags = (
  result: PagePostResponse | undefined,
): Array<TagDescription<'Post-item'>> => [
  ...(result?.content ?? []).reduce<Array<TagDescription<'Post-item'>>>((tags, { id }) => {
    if (id !== undefined) {
      tags.push({ type: 'Post-item' as const, id })
    }
    return tags
  }, []),
  { type: 'Post-item' as const, id: 'LIST' },
]

const postsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    posts: builder.query<PagePostResponse, string>({
      query: (params: string) => ({
        method: 'GET',
        url: `api/posts${params ? `?${params}` : ''}`,
      }),
      providesTags: postsListTags,
      async onQueryStarted(params, { dispatch, getState, queryFulfilled }) {
        let page: PagePostResponse

        try {
          page = (await queryFulfilled).data
        } catch (error) {
          return
        }

        trackedIn(getState()).forEach((post) => {
          const served = page.content?.find(({ id }) => id === post.id)

          if (served?.status !== undefined && TERMINAL_STATUSES.includes(served.status)) {
            dispatch(untrackId(post.id as string))
            sendPostNotification(dispatch, served)
            dispatch(postsApi.util.invalidateTags(['Post-statistics']))
            return
          }

          if (matchesCachedList(params, post)) {
            dispatch(
              postsApi.util.updateQueryData('posts', params, (draft) => {
                applyPostToPage(draft, post)
              }),
            )
          }
        })
      },
    }),
    publishingUpdates: builder.query<null, void>({
      keepUnusedDataFor: PUBLISHING_STREAM_GRACE_SECONDS,
      queryFn: () => ({ data: null }),
      async onCacheEntryAdded(_params, { dispatch, getState, cacheEntryRemoved }) {
        const isStillTracked = (postId: string): boolean =>
          trackedIn(getState()).some(({ id }) => id === postId)

        const apply = (post: PostResponse): void => {
          const postId = post.id

          if (postId === undefined || post.status === undefined) {
            return
          }

          const outOfReach: string[] = []

          cachedListsIn(getState()).forEach((params) => {
            let reached = false

            dispatch(
              postsApi.util.updateQueryData('posts', params, (draft) => {
                const alreadyListed = draft.content?.some(({ id }) => id === postId) ?? false

                if (alreadyListed && !belongsToCachedList(params, post)) {
                  removePostFromPage(draft, postId)
                  reached = true
                  return
                }

                if (alreadyListed || matchesCachedList(params, post)) {
                  applyPostToPage(draft, post)
                  reached = true
                }
              }),
            )

            if (!reached && belongsToCachedList(params, post)) {
              outOfReach.push(params)
            }
          })

          if (!TERMINAL_STATUSES.includes(post.status)) {
            if (
              isStillTracked(postId) &&
              CAN_BE_PLACED_IN_THE_PUBLISHED_LIST.includes(post.status)
            ) {
              dispatch(trackId(post))
            }
            return
          }

          dispatch(postsApi.util.invalidateTags(['Post-statistics']))

          outOfReach.forEach((params) => {
            void dispatch(
              postsApi.endpoints.posts.initiate(params, { subscribe: false, forceRefetch: true }),
            )
          })

          if (isStillTracked(postId)) {
            dispatch(untrackId(postId))
            sendPostNotification(dispatch, post)

            if (post.status === 'SENT') {
              dispatch(requestFeedbackPrompt())
            }
          }
        }

        let stream: EventSource | undefined
        let reopening: ReturnType<typeof setTimeout> | undefined
        let reopenAfterMs = REOPEN_STREAM_AFTER_MS
        let stopped = false

        const pageIsVisible = (): boolean => document.visibilityState !== 'hidden'

        const shouldListen = (): boolean => !stopped && pageIsVisible() && stream === undefined

        const listen = (): void => {
          const opened = new EventSource('/api/posts/sse/subscribe', { withCredentials: true })
          stream = opened

          opened.onopen = () => {
            reopenAfterMs = REOPEN_STREAM_AFTER_MS
          }

          opened.addEventListener('post-update', (event) => {
            apply(JSON.parse((event as MessageEvent<string>).data) as PostResponse)
          })

          opened.onerror = () => {
            if (stream !== opened || opened.readyState !== STREAM_CLOSED) {
              return
            }

            stream = undefined
            reopening = setTimeout(() => {
              reopening = undefined
              void reopenWhileSessionHolds()
            }, reopenAfterMs)
            reopenAfterMs = Math.min(reopenAfterMs * 2, REOPEN_STREAM_AT_MOST_AFTER_MS)
          }
        }

        const quiet = (): void => {
          clearTimeout(reopening)
          reopening = undefined
          stream?.close()
          stream = undefined
        }

        const sessionHolds = async (): Promise<boolean> => {
          const checked = await fetch('/api/user', { credentials: 'include' }).catch(
            () => undefined,
          )

          if (checked === undefined || !SESSION_REJECTED.includes(checked.status)) {
            return true
          }

          const renewing = dispatch(
            rootApi.endpoints.user.initiate(undefined, { forceRefetch: true }),
          )

          try {
            await renewing.unwrap()
            return true
          } catch (error) {
            return false
          } finally {
            renewing.unsubscribe()
          }
        }

        const reopenWhileSessionHolds = async (): Promise<boolean> => {
          if (!shouldListen() || !(await sessionHolds()) || !shouldListen()) {
            return false
          }

          listen()
          return true
        }

        const catchUp = async (): Promise<void> => {
          const trackedIds = trackedIn(getState())
            .map(({ id }) => id)
            .filter((id): id is string => id !== undefined)

          await Promise.all(
            trackedIds.map(async (id) => {
              const fetching = dispatch(
                postsApi.endpoints.post.initiate(id, { forceRefetch: true }),
              )

              try {
                apply(await fetching.unwrap())
              } catch (error) {
                if ((error as FetchBaseQueryError)?.status === 404) {
                  dispatch(untrackId(id))

                  cachedListsIn(getState()).forEach((params) => {
                    dispatch(
                      postsApi.util.updateQueryData('posts', params, (draft) => {
                        removePostFromPage(draft, id)
                      }),
                    )
                  })
                }
              } finally {
                fetching.unsubscribe()
              }
            }),
          )
        }

        const followVisibility = (): void => {
          if (!pageIsVisible()) {
            quiet()
            return
          }

          if (reopening !== undefined) {
            return
          }

          reopenAfterMs = REOPEN_STREAM_AFTER_MS
          void reopenWhileSessionHolds().then(async (reopened) => {
            if (reopened) {
              await catchUp()
            }
          })
        }

        document.addEventListener('visibilitychange', followVisibility)

        if (pageIsVisible()) {
          listen()
        }

        await catchUp()

        await cacheEntryRemoved
        stopped = true
        document.removeEventListener('visibilitychange', followVisibility)
        quiet()
      },
    }),
    post: builder.query<PostResponse, string>({
      query: (postId: string) => `api/posts/${postId}`,
      providesTags: (result, error, postId) => [{ type: 'Post-item', id: postId }],
    }),
    createInstagramPost: builder.mutation<
      PostResponse,
      Draftable<PatchProduct<InstagramPostCreateRequest>>
    >({
      query: ({ draft, body }) => ({
        url: `api/posts/instagram`,
        method: 'POST',
        params: { draft },
        body,
      }),
      invalidatesTags: POST_MUTATION_INVALIDATES,
    }),
    createFacebookPost: builder.mutation<
      PostResponse,
      Draftable<PatchProduct<FacebookPostCreateRequest>>
    >({
      query: ({ draft, body }) => ({
        url: `api/posts/facebook`,
        method: 'POST',
        params: { draft },
        body,
      }),
      invalidatesTags: POST_MUTATION_INVALIDATES,
    }),
    editFacebookPost: builder.mutation<PostResponse, PatchProduct<FacebookPostUpdateRequest>>({
      query: (body) => ({
        url: `api/posts/facebook`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: POST_MUTATION_INVALIDATES,
    }),
    editInstagramPost: builder.mutation<PostResponse, PatchProduct<InstagramPostUpdateRequest>>({
      query: (body) => ({
        url: 'api/posts/instagram',
        method: 'PUT',
        body,
      }),
      invalidatesTags: POST_MUTATION_INVALIDATES,
    }),
    createTiktokPost: builder.mutation<
      PostResponse,
      Draftable<PatchProduct<TiktokPostCreateRequest>>
    >({
      query: ({ draft, body }) => ({
        url: `api/posts`,
        method: 'POST',
        params: { draft },
        body,
      }),
      invalidatesTags: POST_MUTATION_INVALIDATES,
    }),
    editTiktokPost: builder.mutation<PostResponse, PatchProduct<TiktokPostUpdateRequest>>({
      query: (body) => ({
        url: `api/posts`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: POST_MUTATION_INVALIDATES,
    }),
    removePosts: builder.mutation<void, string | string[]>({
      query: (postIdOrIds) =>
        Array.isArray(postIdOrIds)
          ? {
              url: 'api/posts',
              method: 'DELETE',
              body: { ids: postIdOrIds },
            }
          : {
              url: `api/posts/${postIdOrIds}`,
              method: 'DELETE',
            },
      invalidatesTags: (result, error, postIdOrIds) => [
        ...(Array.isArray(postIdOrIds)
          ? postIdOrIds.map((id) => ({ type: 'Post-item' as const, id }))
          : [{ type: 'Post-item' as const, id: postIdOrIds }]),
        { type: 'Post-statistics' as const },
      ],
    }),
    postsStatistics: builder.query<Record<string, number>, void>({
      keepUnusedDataFor: 0,
      query: () => ({
        method: 'GET',
        url: 'api/posts/statistics',
      }),
      providesTags: ['Post-statistics'],
    }),
    movePostToDraft: builder.mutation({
      query: (postId: string) => ({
        method: 'PUT',
        url: `api/posts/${postId}/draft`,
      }),
      invalidatesTags: POST_MUTATION_INVALIDATES,
    }),
    sendPost: builder.mutation({
      query: ({ body }) => ({
        method: 'POST',
        url: 'api/posts/send',
        body,
      }),
      invalidatesTags: POST_MUTATION_INVALIDATES,
    }),
  }),
})

export const {
  usePostsQuery,
  useCreateInstagramPostMutation,
  useCreateTiktokPostMutation,
  usePostQuery,
  useEditInstagramPostMutation,
  useEditTiktokPostMutation,
  useEditFacebookPostMutation,
  useCreateFacebookPostMutation,
  useRemovePostsMutation,
  usePostsStatisticsQuery,
  useMovePostToDraftMutation,
  useSendPostMutation,
  usePublishingUpdatesQuery,
} = postsApi

export default postsApi
